import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../api';
import { renderDescriptionHtml } from '../../utils/renderDescription';
import './Admin.css';

const IMAGE_BASE_URL = 'https://admin.tablemate.net/sites/default/files';

export default function AdminArticleVersions() {
  const { nid } = useParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionContent, setVersionContent] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const versionContentRef = useRef(null);

  // Mark portrait images in version content
  useEffect(() => {
    if (!versionContent || !versionContentRef.current) return;
    const container = versionContentRef.current;
    const imgs = container.querySelectorAll('img');
    const checkPortrait = (img) => {
      if (img.naturalWidth && img.naturalHeight && img.naturalHeight > img.naturalWidth) {
        img.classList.add('img-portrait');
      }
    };
    imgs.forEach((img) => {
      if (img.complete) {
        checkPortrait(img);
      } else {
        img.addEventListener('load', () => checkPortrait(img));
      }
    });
  }, [versionContent]);

  const refresh = useCallback(() => {
    if (!nid) return;
    setLoading(true);
    api.get(`/api/article_versions.php?article_id=${nid}`)
      .then((data) => { setList(Array.isArray(data) ? data : []); setSelectedVersion(null); setVersionContent(null); setMessage({ type: '', text: '' }); })
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [nid]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loadVersion = (versionId) => {
    setSelectedVersion(versionId);
    setExpanded(false);
    const url = versionId === 0 ? `/api/article_version.php?id=0&article_id=${nid}` : `/api/article_version.php?id=${versionId}`;
    api.get(url)
      .then((data) => setVersionContent(data))
      .catch(() => setVersionContent(null));
  };

  const revertToVersion = async (versionId) => {
    if (!window.confirm('Revert article to this version? The current content will be replaced.')) return;
    setReverting(true);
    setMessage({ type: '', text: '' });
    try {
      await api.post(`/api/article_version.php?id=${versionId}`, {});
      setMessage({ type: 'success', text: 'Article reverted successfully.' });
      refresh();
    } catch (err) {
      setMessage({ type: 'error', text: err?.error || err?.message || 'Revert failed' });
    } finally {
      setReverting(false);
    }
  };

  const createdByDisplay = (v) => {
    if (v.created_by_name) return v.created_by_name;
    if (v.created_by_email) return v.created_by_email;
    return '—';
  };

  const openVersionInNewTab = () => {
    if (!versionContent) return;
    const bodyHtml = renderDescriptionHtml(versionContent.description, IMAGE_BASE_URL);
    const posterUrl = versionContent.poster_image || versionContent.posterImage;
    const videoUrl = versionContent.video_url || versionContent.videoUrl;
    const videoHtml = (() => {
      if (!videoUrl) return '';
      const yt = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
      const vimeo = videoUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (yt) return `<iframe src="https://www.youtube.com/embed/${yt[1]}" allowfullscreen frameborder="0" style="max-width:100%;width:100%;aspect-ratio:16/9;min-height:200px"></iframe>`;
      if (vimeo) return `<iframe src="https://player.vimeo.com/video/${vimeo[1]}" allowfullscreen frameborder="0" style="max-width:100%;width:100%;aspect-ratio:16/9;min-height:200px"></iframe>`;
      return `<video src="${videoUrl}" controls${posterUrl ? ` poster="${posterUrl}"` : ''} style="max-width:100%"></video>`;
    })();
    const esc = (s) => (s || '').replace(/</g, '&lt;').replace(/"/g, '&quot;');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Version ${versionContent.version_number}: ${esc(versionContent.title)}</title><style>
      body{font-family:system-ui,sans-serif;max-width:720px;margin:0 auto;padding:1.5rem;line-height:1.6;color:#333}
      h1{font-size:1.5rem;margin:0 0 1rem}
      .meta{color:#666;font-size:0.9rem;margin-bottom:1rem}
      .summary{background:#f8f9fa;padding:0.75rem;border-radius:6px;margin-bottom:1rem}
      .poster img{max-width:100%;max-height:300px;object-fit:contain;border-radius:6px}
      .video iframe,.video video{max-width:100%;width:100%;aspect-ratio:16/9;min-height:200px;border-radius:6px}
      .body img{max-width:100%;height:auto}
      .footer{margin-top:1rem;padding-top:1rem;border-top:1px solid #eee;font-size:0.9rem;color:#666}
    </style></head><body>
      <h1>${esc(versionContent.title) || '(Untitled)'}</h1>
      <div class="meta"><strong>Version:</strong> ${versionContent.version_number} &nbsp; <strong>Saved:</strong> ${esc(versionContent.created_at)} &nbsp; <strong>By:</strong> ${esc(createdByDisplay(versionContent))}</div>
      ${versionContent.summary ? `<div class="summary"><strong>Summary:</strong> ${esc(versionContent.summary)}</div>` : ''}
      ${posterUrl ? `<div class="poster"><img src="${posterUrl}" alt="Poster" /></div>` : ''}
      ${videoUrl ? `<div class="video">${videoHtml}</div>` : ''}
      ${bodyHtml ? `<div class="body">${bodyHtml}</div>` : ''}
      ${(versionContent.tags || versionContent.audience || versionContent.article_link || versionContent.articleLink) ? `<div class="footer">${versionContent.tags ? `<div><strong>Tags:</strong> ${esc(typeof versionContent.tags === 'string' ? versionContent.tags : (Array.isArray(versionContent.tags) ? versionContent.tags.join(', ') : ''))}</div>` : ''}${versionContent.audience ? `<div><strong>Audience:</strong> ${esc(versionContent.audience)}</div>` : ''}${(versionContent.article_link || versionContent.articleLink) ? `<div><strong>Article link:</strong> <a href="${esc(versionContent.article_link || versionContent.articleLink)}">${esc(versionContent.article_link || versionContent.articleLink)}</a></div>` : ''}</div>` : ''}
    </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="admin-section">
      <div className="admin-toolbar">
        <Link to="/admin/articles" className="admin-btn admin-btn-secondary">← Back to articles</Link>
      </div>
      <h1>Version history — Article #{nid}</h1>
      {message.text && <div className={`admin-message ${message.type}`}>{message.text}</div>}
      {loading ? <p>Loading…</p> : (
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div className="admin-table-wrap" style={{ flex: '1 1 320px' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Title</th>
                  <th>Created by</th>
                  <th>Saved at</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map((v) => (
                  <tr key={v.id}>
                    <td>{v.version_number}</td>
                    <td>{(v.title || '').slice(0, 50)}</td>
                    <td>{createdByDisplay(v)}</td>
                    <td>{v.created_at ? new Date(v.created_at).toLocaleString() : '—'}</td>
                    <td>
                      <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={() => loadVersion(v.id)}>View</button>
                      <button type="button" className="admin-btn admin-btn-primary admin-btn-small" onClick={() => revertToVersion(v.id)} disabled={reverting || v.id === 0} title={v.id === 0 ? 'Current version (no revert needed)' : ''}>Revert</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.length === 0 && <p style={{ padding: '1rem' }}>No versions yet.</p>}
          </div>
          {versionContent && (
            <div className={`admin-form ${expanded ? 'admin-version-expanded' : ''}`} style={{ flex: '1 1 400px', maxWidth: expanded ? '100%' : '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h2 style={{ margin: 0 }}>Version snapshot</h2>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={openVersionInNewTab} title="Open in new tab">↗ New tab</button>
                  <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={() => setExpanded((e) => !e)} title={expanded ? 'Collapse' : 'Expand'}>
                    {expanded ? '⊟ Collapse' : '⊞ Expand'}
                  </button>
                </div>
              </div>
              <p><strong>Title:</strong> {versionContent.title}</p>
              <p><strong>Saved:</strong> {versionContent.created_at}</p>
              <p><strong>Created by:</strong> {createdByDisplay(versionContent)}</p>
              <div ref={versionContentRef} style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: 4, overflow: 'auto', maxHeight: expanded ? 'none' : 400 }}>
                <div dangerouslySetInnerHTML={{ __html: renderDescriptionHtml(versionContent.description, IMAGE_BASE_URL) || '(no content)' }} />
              </div>
              <div style={{ marginTop: '1rem' }}>
                <button type="button" className="admin-btn admin-btn-primary" onClick={() => revertToVersion(versionContent.id)} disabled={reverting || versionContent.id === 0} title={versionContent.id === 0 ? 'Current version (no revert needed)' : ''}>
                  {reverting ? 'Reverting…' : 'Revert to this version'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
